import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Checkbox} from 'primereact/checkbox';
import PropTypes from 'prop-types';
import {Column} from 'primereact/column';
import {DataTable} from 'primereact/datatable';
import {Button as ButtonPrime} from 'primereact/button';
import {ProgressSpinner} from 'primereact/progressspinner';
import Button from '../core/button';
import {
    clearBulkEids,
    clearGraceNoteResponse,
    convertGuidsToEids,
    sendToGraceNote,
} from '../../actions/grace-note-actions';

const styles = {
    div: {marginLeft: -8},
    group: {height: 27, maxHeight: 27, minHeight: 27, marginTop: 10},
    input: {marginLeft: -2},
    span: {marginLeft: 5, fontSize: 14},
    error: {fontSize: 14, color: 'red', paddingTop: 10},
    timedMessage: {fontSize: 14, color: 'green', marginTop: 10},
    table: {marginTop: 10, width: 395},
    column: {textAlign: 'center'},
    refresh: {width: 30, height: 30, marginTop: -45, position: 'absolute', marginLeft: 20},
    spinner: {width: 15, height: 15, marginTop: 15},
    gnButton: {marginTop: 5, fontSize: 14},
};

function GraceNoteControlsGroup({ isMoviesAndPrograms }) {
  const dispatch = useDispatch();
  const { content } =        useSelector((state) => (isMoviesAndPrograms
                ? state?.servicesSearchData?.moviesAndProgramsDetail?.searchData
                : state?.servicesSearchData?.mediaItemDetail?.searchData
        ) || null);
  const { response, error } = useSelector((state) => state?.graceNote?.graceNote) || { response: null, error: null };
  const { graceNoteGuids } = useSelector((state) => state?.graceNote) || [];
  const { echostarIds } = useSelector((state) => state?.graceNote) || [];
  const [echostarId, setEchostarId] = useState([]);
  const [timedMessage, setTimedMessage] = useState(null);
  const [showGnControls, setShowGNControls] = useState(false);
  const [startProgress, setStartProgress] = useState(false);
  const [notShell, setNotShell] = useState(true);

  const convertGuidsToEidsFunc = () => {
    if (graceNoteGuids?.length) {
      return dispatch(convertGuidsToEids(graceNoteGuids));
    }
  };

  const showTimedMessage = (message) => {
    setTimedMessage(message);
    if (message) {
      setTimeout(() => {
        setTimedMessage(null);
        dispatch(clearGraceNoteResponse());
      }, 5000);
    }
  };

  useEffect(() => {
    convertGuidsToEidsFunc();
  }, [graceNoteGuids]);

  useEffect(() => {
    if (content?.length && content?.length === 1) {
      const { amsEpisode, amsFranchise } = content[0];
      const eId = [];
      if (!amsEpisode && !amsFranchise) {
        eId.push(content[0]?.echostarId);
        setNotShell(false);
      } else {
        const episodeIsNotShell = amsEpisode
          ? !!(
            amsEpisode?.tmsId?.toString().length
                          || amsEpisode?.roviProgramId?.toString().length
                          || amsEpisode?.sourceId?.toString().length
          )
          : false;
        const franchiseIsNotShell = amsFranchise
          ? !!(
            amsFranchise?.tmsId?.toString().length
                          || amsFranchise?.roviProgramId?.toString().length
                          || amsFranchise?.sourceId?.toString().length
          )
          : false;

        if (episodeIsNotShell && franchiseIsNotShell) {
          setNotShell(true);
        } else {
          if (amsEpisode && amsEpisode.echostarId && !episodeIsNotShell) {
            eId.push(amsEpisode.echostarId);
            setNotShell(false);
          }

          if (amsFranchise && amsFranchise.echostarId && !franchiseIsNotShell) {
            eId.push(amsFranchise.echostarId);
            setNotShell(false);
          }

          if (!eId.length) {
            setNotShell(true);
          }
        }
      }
      setEchostarId(eId.filter((e) => e && e.toString().length));
    }
  }, [content]);

  useEffect(() => {
    setStartProgress(false);
    showTimedMessage(!response ? null : `${response?.target} completed!`);
  }, [response]);

  useEffect(() => {
    setStartProgress(false);
  }, [error]);

  const [priority, setPriority] = useState(false);

  const table = (data, header) => (
        <DataTable
            paginator={true}
            rows={5}
            stripedRows={true}
            style={styles.table}
            value={data.map((e) => ({ echostarId: e }))}
        >
            <Column field="echostarId" header={header} sortable={true} style={styles.column} />
        </DataTable>
  );

  return (
        <>
            {echostarId && echostarId.length ? (
                <div className="p-col-12 p-md-12" style={styles.div}>
                    <ButtonPrime
                        className="p-button-text"
                        icon={showGnControls ? 'pi pi-minus' : 'pi pi-plus'}
                        label={`${showGnControls ? 'Hide' : 'Show'} GraceNote Controls`}
                        style={styles.gnButton}
                        onClick={() => {
                          setShowGNControls(!showGnControls);
                        }}
                    />
                    {showGnControls ? (
                        <>
                            <div className="p-inputgroup" style={styles.group}>
                                <Button
                                    disabled={notShell || !echostarId.filter((e) => e)?.length || response != null}
                                    onClick={() => {
                                      const toBeSent = [...new Set([...echostarId, ...echostarIds])].filter(
                                        (s) => s && s.toString().length,
                                      );
                                      if (toBeSent?.length) {
                                        setStartProgress(true);
                                        return dispatch(
                                          sendToGraceNote({
                                            echostarIds: toBeSent,
                                            priority,
                                          }),
                                        );
                                      }
                                    }}
                                >
                                    Send to GraceNote
                                </Button>
                                <span className="p-inputgroup-addon" style={styles.input}>
                                    <Checkbox checked={priority} onChange={() => setPriority(!priority)} />
                                    <span style={styles.span}>High priority</span>
                                </span>
                                <Button disabled={!echostarIds?.length} onClick={() => dispatch(clearBulkEids())}>
                                    Clear Bulk IDs
                                </Button>
                            </div>
                            {startProgress ? <ProgressSpinner style={styles.spinner} /> : null}
                            {error !== null ? <span style={styles.error}>{error}</span> : null}
                            {timedMessage !== null ? <div style={styles.timedMessage}>{timedMessage}</div> : null}
                            {echostarId
                              ? table(
                                echostarId,
                                `${notShell ? '(Not a Shell)  ' : ''}Local EchostarID for GraceNote`,
                              )
                              : null}
                            {echostarIds && echostarIds.length ? (
                                <>
                                    {table(
                                      [...new Set(echostarIds)].filter((s) => s),
                                      'Bulk EchostarID for GraceNote'
                                    )}
                                    <ButtonPrime
                                        className="p-button-rounded p-button-outlined"
                                        icon="pi pi-refresh"
                                        style={styles.refresh}
                                        onClick={() => convertGuidsToEidsFunc()}
                                    />
                                </>
                            ) : null}
                        </>
                    ) : null}
                </div>
            ) : null}
        </>
  );
}

GraceNoteControlsGroup.propTypes = {
    isMoviesAndPrograms: PropTypes.bool,
};

export default GraceNoteControlsGroup;
